import { useRef, useCallback } from 'react'
import { File, Eye } from 'lucide-react'
import { CARD_WIDTH } from './types'
import type { CardPosition } from './types'

interface PageCardProps {
  docId: string
  docName: string
  position: CardPosition
  isSelected: boolean
  onSelect: () => void
  onMove: (pos: CardPosition) => void
  onPreview: () => void
  onPortClick: (docId: string, side: 'left' | 'right') => void
  showPorts: boolean
}

export default function PageCard({
  docId,
  docName,
  position,
  isSelected,
  onSelect,
  onMove,
  onPreview,
  onPortClick,
  showPorts,
}: PageCardProps) {
  const cardRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<{ isDragging: boolean; startX: number; startY: number; origX: number; origY: number }>({
    isDragging: false, startX: 0, startY: 0, origX: 0, origY: 0,
  })

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Don't start drag from eye button or ports
    if ((e.target as HTMLElement).closest('[data-no-drag]')) return
    e.preventDefault()
    onSelect()

    const drag = dragRef.current
    drag.isDragging = true
    drag.startX = e.clientX
    drag.startY = e.clientY
    drag.origX = position.x
    drag.origY = position.y

    const onMouseMove = (ev: MouseEvent) => {
      if (!drag.isDragging || !cardRef.current) return
      const dx = ev.clientX - drag.startX
      const dy = ev.clientY - drag.startY
      cardRef.current.style.left = `${drag.origX + dx}px`
      cardRef.current.style.top = `${drag.origY + dy}px`
    }

    const onMouseUp = (ev: MouseEvent) => {
      if (!drag.isDragging) return
      drag.isDragging = false
      const dx = ev.clientX - drag.startX
      const dy = ev.clientY - drag.startY
      onMove({ x: drag.origX + dx, y: drag.origY + dy })
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
  }, [position.x, position.y, onSelect, onMove])

  return (
    <div
      ref={cardRef}
      className="absolute select-none"
      style={{ left: position.x, top: position.y, width: CARD_WIDTH }}
    >
      {/* Connection port dots at edge centers */}
      {showPorts && (
        <>
          {/* Top port */}
          <span className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 z-10 size-2 rounded-full bg-[#0e7490]" />
          {/* Bottom port */}
          <span className="absolute left-1/2 bottom-0 -translate-x-1/2 translate-y-1/2 z-10 size-2 rounded-full bg-[#0e7490]" />
          {/* Left port */}
          <button
            data-no-drag
            type="button"
            className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 size-2 rounded-full bg-[#0e7490] hover:scale-150 transition-transform"
            onClick={(e) => { e.stopPropagation(); onPortClick(docId, 'left') }}
          />
          {/* Right port */}
          <button
            data-no-drag
            type="button"
            className="absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 z-10 size-2 rounded-full bg-[#0e7490] hover:scale-150 transition-transform"
            onClick={(e) => { e.stopPropagation(); onPortClick(docId, 'right') }}
          />
        </>
      )}

      {/* Card body */}
      <div
        onMouseDown={handleMouseDown}
        className={`flex items-start gap-3 rounded-lg bg-white dark:bg-dark-card cursor-grab active:cursor-grabbing transition-colors ${
          isSelected ? 'border-2 border-[#0e7490] p-[19px]' : 'border border-border hover:border-muted-foreground/30 p-5'
        }`}
      >
        <File className="size-5 text-muted-foreground shrink-0" />
        <span className="flex-1 min-w-0 text-sm font-medium text-foreground truncate">
          {docName}
        </span>
        <button
          data-no-drag
          type="button"
          onClick={(e) => { e.stopPropagation(); onPreview() }}
          className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
        >
          <Eye className="size-5" />
        </button>
      </div>
    </div>
  )
}
